import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardListIcon, InfoIcon } from 'lucide-react';
interface MerchantRequest {
  id: string;
  type: string;
  description: string;
  requestedBy: string;
  requestedAt: string;
  status: string;
  details: {
    paymentMethod?: string;
    roleName?: string;
    reason?: string;
    permissions?: string;
    rejectionReason?: string;
  };
}
export function Requests() {
  const navigate = useNavigate();
  const [merchantRequests] = useState<MerchantRequest[]>(() => {
    const now = new Date();
    // Pull any pending payout-account update submitted from Settings
    let payoutItems: MerchantRequest[] = [];
    try {
      const raw = sessionStorage.getItem('pendingPayoutUpdate');
      if (raw) {
        const p = JSON.parse(raw);
        payoutItems = [{
          id: p.id || 'PAYOUT-REQ',
          type: 'Bank Account Update',
          description: `Update payout bank account to ${p.bank} ${p.maskedAccount}`,
          requestedBy: 'Simon Lee',
          requestedAt: p.submittedAt || new Date().toISOString(),
          status: 'Pending',
          details: {
            reason: 'Merchant-initiated payout account change'
          }
        }];
      }
    } catch {
      // ignore parse errors
    }
    return [...payoutItems, {
      id: 'REQ-001',
      type: 'Payment Method Activation',
      description: 'Request to activate ShopeePay payment method',
      requestedBy: 'Simon Lee',
      requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        paymentMethod: 'ShopeePay',
        reason: 'Customer demand for ShopeePay payments'
      }
    }, {
      id: 'REQ-002',
      type: 'User Role Request',
      description: 'Request for custom "Customer Support" role',
      requestedBy: 'Sarah Tan',
      requestedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        roleName: 'Customer Support',
        permissions: 'Read-only access to payments and customers'
      }
    }, {
      id: 'REQ-003',
      type: 'Payment Method Activation',
      description: 'Request to activate Boost eWallet',
      requestedBy: 'Ahmad Rahman',
      requestedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'Boost',
        reason: 'Expand payment options for customers',
        rejectionReason: 'Merchant category not supported by Boost'
      }
    }, {
      id: 'REQ-004',
      type: 'Payment Method Activation',
      description: 'Request to activate American Express',
      requestedBy: 'Simon Lee',
      requestedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'In Progress',
      details: {
        paymentMethod: 'American Express',
        reason: 'Corporate clients prefer Amex'
      }
    }, {
      id: 'REQ-005',
      type: 'User Role Request',
      description: 'Request for "Marketing" role with limited access',
      requestedBy: 'Sarah Tan',
      requestedAt: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        roleName: 'Marketing',
        permissions: 'Read access to reports and customer data'
      }
    }, {
      id: 'REQ-006',
      type: 'Payment Method Activation',
      description: 'Request to activate DuitNow QR',
      requestedBy: 'Ahmad Rahman',
      requestedAt: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'DuitNow QR',
        reason: 'Low-cost payment option for customers'
      }
    }].sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
  });
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Closed':
        return 'bg-gray-100 text-gray-800';
      case 'Pending':
        return 'bg-amber-100 text-amber-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };
  return <div className="max-w-7xl mx-auto py-8 px-4" data-id="element-12933">
      <div className="mb-8" data-id="element-12934">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-12935">
          Merchant Requests
        </h1>
        <p className="text-gray-600" data-id="element-12936">View & track requests</p>
      </div>

      <div className="space-y-6" data-id="element-12937">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-12938">
          <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-12939">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-12940">
              Total Requests
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-12941">
              {merchantRequests.length}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-12942">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-12943">
              Pending
            </p>
            <p className="text-2xl font-bold text-amber-600" data-id="element-12944">
              {merchantRequests.filter(r => r.status === 'Pending').length}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-12945">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-12946">
              Closed
            </p>
            <p className="text-2xl font-bold text-gray-600" data-id="element-12947">
              {merchantRequests.filter(r => r.status === 'Closed').length}
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-12948">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-12949">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-12950">
              <ClipboardListIcon size={18} className="text-gray-600" data-id="element-12951" />
              All Requests ({merchantRequests.length})
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-12952">
            <table className="w-full" data-id="element-12953">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-12954">
                <tr data-id="element-12955">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12956">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12957">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12958">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12959">
                    Requested By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12960">
                    Requested At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12961">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-12962">
                {merchantRequests.map(request => <tr key={request.id} onClick={() => navigate(`/requests/${request.id}`)} className="hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-12963">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono" data-id="element-12964">
                      {request.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-12965">
                      {request.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs" data-id="element-12966">
                      {request.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-12967">
                      {request.requestedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" title={new Date(request.requestedAt).toLocaleString()} data-id="element-12968">
                      {getRelativeTime(request.requestedAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-12969">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-12970">
                        {request.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}